import cn from 'classnames';
import { debounce } from 'lodash';
import React, { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import {
  ASIDE_MENU_WIDTH,
  ASIDE_WIDTH_SET_DELAY,
  Direction,
  Modal,
  NAV_ANIMATION_DELAY,
} from '@const/consts';
import { Path, Section } from '@const/paths';

import UsersService from '@services/users';

import { useAppSelector, useAppDispatch } from '@hooks/redux';

import {
  Aside,
  Button,
  DashedLine,
  ILibNavItem,
  LibNavItemLevel,
  LibNavigationTheme,
  Navigation,
} from '@infologistics/frontend-libraries';
import MenuItem from './components/MenuItem';

import styles from './Menu.module.scss';

import { setMenuItems } from '@store/modules/navigation';
import {
  resetError403,
  resetError404,
  setPrevError,
} from '@store/modules/utils';

import { INavItem } from './types';
import { setUserSettings } from '@store/modules/user';
import { displayErrorNotification } from '@utils/utils';
import { addBoxMenuItems } from './utils';
import { setModal } from '@app/store/modules/modal';
import ModalWrapper from '@app/common/ModalWrapper';
import { BusinessBoxCreate } from '@app/views/boxes/components/BusinessBoxCreate';
import { IBoxType } from '@app/store/modules/boxes/types';

const Menu: FC = () => {
  const dispatch = useAppDispatch();
  const { activeMenu, menuItems } = useAppSelector((state) => state.navigation);
  const { isAuthenticated, profile, settings: userSettings } = useAppSelector((state) => state.user);
  const { boxes } = useAppSelector((state) => state.boxes);
  const { isError404, isError403, language } = useAppSelector(
    (state) => state.utils
  );
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const refButtonBusinessBox: React.Ref<HTMLButtonElement> = useRef(null);

  const getExtendedMenuItems = (
    menuItems: INavItem[],
    level = LibNavItemLevel.TOP
  ): ILibNavItem[] => {
    const activeMenuPathname = activeMenu || location.pathname;

    const activeMenuItems = menuItems.filter(({ route }) =>
      activeMenuPathname.includes(route)
    );

    const activeMenuItemUuid = activeMenuItems.length
      ? activeMenuItems[0].uuid
      : '';

    return menuItems.map(
      (item): ILibNavItem =>
        getExtendedMenuItem(item, level, activeMenuItemUuid)
    );
  };

  const getExtendedMenuItem = (
    menuItem: INavItem,
    menuLevel: string,
    activeMenuItemUuid: string
  ): ILibNavItem => {
    const { items, title, uuid } = menuItem;

    const isActive = activeMenuItemUuid === uuid;
    const isTopLevel = menuLevel === LibNavItemLevel.TOP;

    const subMenuItemsForDropdown =
      items && getExtendedMenuItems(items, LibNavItemLevel.TOP);

    return {
      ...menuItem,
      customNavLink: <MenuItem key={uuid} item={menuItem} />,
      isActive,
      isOpen: !isTopLevel || isActive,
      items: subMenuItemsForDropdown,
      level: menuLevel,
      title: t(title),
    };
  };

  const getNavItems = (): void => {
    const { boxOguid, email: profileEmail, fullName } = profile;
    const email = boxes.find((val) => val.type === IBoxType.PERSONAL)?.email ?? profileEmail;

    const items: INavItem[] = [
      {
        contentData: {
          content: email,
          heading: fullName,
        },
        iconCollapse: 'IconAngle',
        iconCollapseProps: { classes: 'ml-auto icon-collapse', size: 'xs' },
        items: [
          {
            icon: 'IconIncomingAlt',
            iconProps: { classes: 'mr-2' },
            route: Path.ENVELOPES_BOX_OGUID.replace(
              ':boxOguid',
              boxOguid
            ).replace(':directionOrFolder', Direction.INCOMING),
            title: 'envelopes:incoming',
            uuid: uuidv4(),
          },
          {
            icon: 'IconOutgoingAlt',
            iconProps: { classes: 'mr-2' },
            route: Path.ENVELOPES_BOX_OGUID.replace(
              ':boxOguid',
              boxOguid
            ).replace(':directionOrFolder', Direction.OUTGOING),
            title: 'envelopes:outgoing',
            uuid: uuidv4(),
          },
        ],
        route: Section.ENVELOPES_BOX_OGUID.replace(':boxOguid', boxOguid),
        title: fullName,
        uuid: uuidv4(),
      },
    ];

    const fullItems = addBoxMenuItems(items, boxes);

    dispatch(setMenuItems(fullItems));
  };

  const handleNewEnvelopeButtonClick = (): void => {
    navigate(Path.ENVELOPE_NEW);
  };

  const handleCreateBusinessBoxButtonClick = (): void => {
    if (refButtonBusinessBox.current) {
      refButtonBusinessBox.current.blur();
    }
    dispatch(setModal(Modal.BUSINESS_BOX));
  };

  const handleResize = (asideWidth: string) => {
    dispatch(setUserSettings({ asideWidth }));

    UsersService.updateUserSettings({...userSettings, asideWidth }).catch(
      displayErrorNotification
    );
  };

  const handleWithDebounse = debounce(handleResize, ASIDE_WIDTH_SET_DELAY);

  useEffect(getNavItems, [language, boxes]);
  useEffect(() => {
    if (!isAuthenticated) return;

    return () => {
      dispatch(setPrevError(isError403 || isError404));
      dispatch(resetError403());
      dispatch(resetError404());
    };
  }, [location.pathname]);

  return (
    <Aside onResize={handleWithDebounse} minWidth={ASIDE_MENU_WIDTH.MIN} maxWidth={ASIDE_MENU_WIDTH.MAX}>
      <div className='d-flex full-width'>
        <Button
          theme='warning'
          classes={cn('fw-700 my-2 mx-auto', styles.new_envelope_button)}
          onClick={handleNewEnvelopeButtonClick}
        >
          {t('envelopes:new')}
        </Button>
      </div>
      <Navigation
        delay={NAV_ANIMATION_DELAY}
        items={getExtendedMenuItems(menuItems)}
        theme={LibNavigationTheme.ALTERNATIVE}
        height={'calc(100% - 2*48px)'}
      />
      <DashedLine />
      <div className={cn('d-flex full-width')}>
        <Button
          ref={refButtonBusinessBox}
          theme='default'
          classes={cn('fw-700 my-2 mx-auto', styles.nav_button)}
          onClick={handleCreateBusinessBoxButtonClick}
          leftIcon='IconCog'
        >
          {t('boxes:create')}
        </Button>
      </div>
      <ModalWrapper id={Modal.BUSINESS_BOX} backdrop={false}>
        <BusinessBoxCreate />
      </ModalWrapper>
    </Aside>
  );
};

export default Menu;
